import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { adminGetSocialLinks, adminSaveSocialLinks } from "../../services/api";

const label = "text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-charcoal";
const input = "mt-2 w-full border border-brand-sand bg-white px-4 py-3 text-sm outline-none focus:border-brand-orange";

const PLATFORMS = [
  { key: "instagram", label: "Instagram", ph: "https://instagram.com/…" },
  { key: "facebook", label: "Facebook", ph: "https://facebook.com/…" },
  { key: "youtube", label: "YouTube", ph: "https://youtube.com/@…" },
  { key: "whatsapp", label: "WhatsApp", ph: "https://wa.me/91…" },
  { key: "telegram", label: "Telegram", ph: "https://t.me/…" },
  { key: "pinterest", label: "Pinterest", ph: "https://pinterest.com/…" },
  { key: "twitter", label: "X / Twitter", ph: "https://x.com/…" },
];

const EMPTY = Object.fromEntries(PLATFORMS.map((p) => [p.key, ""]));

export default function AdminSocial() {
  const { user, ready } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ready && user?.role === "admin") {
      adminGetSocialLinks()
        .then((d) => setForm({ ...EMPTY, ...d }))
        .catch(() => toast.error("Could not load social links."))
        .finally(() => setLoading(false));
    }
  }, [ready, user]);

  const save = async () => {
    setSaving(true);
    try {
      await adminSaveSocialLinks(form);
      toast.success("Social links saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout
      title="Social Links"
      subtitle="Footer social URLs — leave blank to keep an icon hidden"
      actions={
        <button onClick={save} disabled={saving || loading} data-testid="social-save-btn" className="bg-brand-charcoal px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-brand-cream transition-colors hover:bg-brand-orange disabled:opacity-50">
          {saving ? "Saving…" : "Save"}
        </button>
      }
    >
      {loading ? (
        <p className="text-brand-stone" data-testid="social-loading">Loading…</p>
      ) : (
        <div className="max-w-2xl space-y-5" data-testid="admin-social-page">
          {PLATFORMS.map((p) => (
            <div key={p.key}>
              <label className={label} htmlFor={`social-${p.key}`}>{p.label}</label>
              <input
                id={`social-${p.key}`}
                data-testid={`social-${p.key}`}
                className={input}
                value={form[p.key] || ""}
                onChange={(e) => setForm((f) => ({ ...f, [p.key]: e.target.value }))}
                placeholder={p.ph}
              />
            </div>
          ))}
          <p className="text-xs text-brand-stone">Icons for blank links stay hidden in the footer until a URL is added.</p>
        </div>
      )}
    </AdminLayout>
  );
}
